import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import useUser from "@/users/useUser";

/**
 * @param {string} featureName - The key of the feature.
 * @returns {[Boolean, {loading: Boolean, error: Error}]} An
 *  array containing first, whether or not the given feature is enabled, which
 *  is `undefined` if the decision is loading. Then next, an object with
 *  `loading`, and `error`. `loading` indicates whether the feature decision is
 *  potentially still loading. `error` includes any errors that may be related to
 *  properly determining feature access.
 */
const useHasFeature = (featureName) => {
  // If an experiment is concluded in our framework, then the next step is to remove any
  // calls to this function related to the experiment, and prune the code paths that are
  // no longer relevant. That said, we may not be able to prioritise the cleanup
  // at the same time the experiment is completely finished. Adding the experiment
  // slug to this array will enable it for everyone without checking the experiment
  // segment a user is allocated to, and acts as a bypass until we have the time to
  // do the appropriate cleanup. Use this sparingly.
  // TODO: There's a bunch of post-Optimizely cleanup needed. See SWE-1304.
  const completedExperiments = [
    "assigning_specific_activities",
    "assignment-settings",
    "flattened_filter",
    "font_size_feature",
    "grading_page",
    "lite_users_to_see_more_text",
    "paragraph_count_feature",
    "powerwords_grading",
    "reading_skills_filter",
    "split_screen_grading",
    "student_preview",
    "video_present_mode",
    "video_view_page",
    "share_assignments",
  ];
  if (completedExperiments.includes(featureName)) {
    return [true, { loading: false, error: null }];
  }

  // Check our in house framework to see if the user has access to the feature, but
  // also check the `features` array on the user themselves.
  // TODO: "Feature" here is very, very conflated vocabulary. Come back and figure this
  // out down the road.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data: user } = useUser();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [segmentName, metadata] = useGetUserExperimentSegment(featureName);
  if (metadata?.loading || metadata?.error) {
    return [undefined, metadata];
  }
  const featureEnabled =
    (segmentName || user?.features?.[featureName]) === "on";
  return [featureEnabled, metadata];
};

export default useHasFeature;
